import {onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useRouter} from 'vue-router'
import {useNotificationStore} from '@toolify/client/src/stores/NotificationStore/useNotificationStore'
import {mapNotificationToItem} from '@toolify/client/src/stores/NotificationStore/singletons/mapNotificationToItem'
import {useNotificationApiStore} from '@toolify/client/src/stores/api/NotificationApiStore/useNotificationApiStore'
import {isElectron} from '@toolify/client/src/modules/electron/singletons/isElectron'
import {i18n} from '@toolify/client/src/setup/i18n'
import type {IFloatingNotificationSyncPayload} from '@toolify/client/src/modules/notifications/types/IFloatingNotificationSyncPayload'
import {useNativeLocalMessages} from '@toolify/client/src/modules/notifications/composables/useNativeLocalMessages'

export function useFloatingNotificationsSync() {
  const isFloatingNotificationsInSeparateWindow = ref(false)
  const isDesktopApp = ref(false)
  const router = useRouter()
  const notificationStore = useNotificationStore()
  const notificationApiStore = useNotificationApiStore()

  const api = typeof window !== 'undefined' ? (window as unknown as { electronAPI?: {
    getFloatingNotificationsWindowMode: () => Promise<boolean>
    syncFloatingNotifications: (data: IFloatingNotificationSyncPayload) => void
    onFloatingNotificationOpen: (fn: (notificationId: string) => void) => void
    offFloatingNotificationOpen: (fn: (notificationId: string) => void) => void
    onFloatingNotificationDismiss: (fn: (notificationId: string) => void) => void
    offFloatingNotificationDismiss: (fn: (notificationId: string) => void) => void
    onFloatingNotificationMarkRead: (fn: (notificationId: string, isAggregationGroup?: boolean) => void) => void
    offFloatingNotificationMarkRead: (fn: (notificationId: string, isAggregationGroup?: boolean) => void) => void
    onFloatingNotificationMarkUnread: (fn: (notificationId: string) => void) => void
    offFloatingNotificationMarkUnread: (fn: (notificationId: string) => void) => void
    onFloatingNotificationHoverStart: (fn: (notificationId: string) => void) => void
    offFloatingNotificationHoverStart: (fn: (notificationId: string) => void) => void
    onFloatingNotificationHoverEnd: (fn: (notificationId: string) => void) => void
    offFloatingNotificationHoverEnd: (fn: (notificationId: string) => void) => void
    onFloatingLocalMessageAction: (fn: (messageId: string, actionIndex: number) => void) => void
    offFloatingLocalMessageAction: (fn: (messageId: string, actionIndex: number) => void) => void
    onFloatingLocalMessageDismiss: (fn: (messageId: string, reason: string) => void) => void
    offFloatingLocalMessageDismiss: (fn: (messageId: string, reason: string) => void) => void
    sendFloatingLocalMessageOpen: (messageId: string) => void
  } }).electronAPI : undefined

  const nativeLocalMessages = useNativeLocalMessages({
    isEnabled: isDesktopApp,
    isInSeparateWindow: isFloatingNotificationsInSeparateWindow,
    onSystemNotificationClick: messageId => api?.sendFloatingLocalMessageOpen(messageId),
  })

  function buildPayload(): IFloatingNotificationSyncPayload {
    const items = notificationStore.popupNotifications
      .map(notification => {
        const notificationItem = mapNotificationToItem(notification)
        if (!notificationItem) return null
        const {onAfterRead: _, ...rest} = notificationItem
        return {
          notificationId: notification.id,
          notificationItem: rest,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
    return {
      items,
      messages: nativeLocalMessages.floatingSyncItems.value,
      theme: document.documentElement.getAttribute('data-theme'),
      locale: i18n.global.locale.value,
    }
  }

  function serializePayload(payload: IFloatingNotificationSyncPayload): IFloatingNotificationSyncPayload {
    return JSON.parse(JSON.stringify(payload)) as IFloatingNotificationSyncPayload
  }

  function sync() {
    if (!api) return
    api.syncFloatingNotifications(serializePayload(buildPayload()))
  }

  function onOpen(notificationId: string) {
    const notification = notificationStore.popupNotifications.find(item => item.id === notificationId)
    notificationStore.dismissPopupNotification(notificationId)
    if (!notification) {
      return
    }
    const notificationItem = mapNotificationToItem(notification)
    const to = notificationItem?.to ?? notificationItem?.detailsTo
    if (to && typeof to === 'object' && to !== null && 'name' in to) {
      router.push(to as Parameters<typeof router.push>[0])
    }
  }

  function onDismiss(notificationId: string) {
    notificationStore.dismissPopupNotification(notificationId)
  }

  async function onMarkRead(notificationId: string, isAggregationGroup?: boolean) {
    if (isAggregationGroup) {
      await notificationApiStore.markNotificationGroupAsRead(notificationId)
    } else {
      await notificationStore.markNotificationAsRead(notificationId)
    }
  }

  async function onMarkUnread(notificationId: string) {
    await notificationStore.markNotificationAsUnread(notificationId)
  }

  function onHoverStart(notificationId: string) {
    notificationStore.setPopupNotificationHovered(notificationId, true)
  }

  function onHoverEnd(notificationId: string) {
    notificationStore.setPopupNotificationHovered(notificationId, false)
  }

  onMounted(async () => {
    if (!isElectron() || !api) return
    const mode = await api.getFloatingNotificationsWindowMode()
    isFloatingNotificationsInSeparateWindow.value = mode
    isDesktopApp.value = true
    if (!mode) return

    sync()
    watch(() => notificationStore.popupNotifications, () => sync(), {deep: true})
    watch(nativeLocalMessages.floatingSyncItems, () => sync())

    api.onFloatingLocalMessageAction(nativeLocalMessages.runAction)
    api.onFloatingLocalMessageDismiss(nativeLocalMessages.dismiss)
    api.onFloatingNotificationOpen(onOpen)
    api.onFloatingNotificationDismiss(onDismiss)
    api.onFloatingNotificationMarkRead(onMarkRead)
    api.onFloatingNotificationMarkUnread(onMarkUnread)
    api.onFloatingNotificationHoverStart(onHoverStart)
    api.onFloatingNotificationHoverEnd(onHoverEnd)
  })

  onBeforeUnmount(() => {
    // off() throws for a listener that was never registered
    if (!api || !isDesktopApp.value || !isFloatingNotificationsInSeparateWindow.value) return
    api.offFloatingLocalMessageAction(nativeLocalMessages.runAction)
    api.offFloatingLocalMessageDismiss(nativeLocalMessages.dismiss)
    api.offFloatingNotificationOpen(onOpen)
    api.offFloatingNotificationDismiss(onDismiss)
    api.offFloatingNotificationMarkRead(onMarkRead)
    api.offFloatingNotificationMarkUnread(onMarkUnread)
    api.offFloatingNotificationHoverStart(onHoverStart)
    api.offFloatingNotificationHoverEnd(onHoverEnd)
  })

  return {isFloatingNotificationsInSeparateWindow}
}
